import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';

import { Logger } from '@futrli/shared-services';
import { UILoader, toastError } from '@futrli/ui-components';
import { DispatchedReportItem } from '@futrli-sdk/report_scheduling';

import { Data, TABLE_COLUMN } from '../../../../utils/table-filters/filter-table-types';
import { Filter } from '../../../../utils/table-filters/components/filter';
import { getDispatchedReports } from '../../../../core/api/reporting/report-scheduling';
import { getHoursMinutes } from '../../../../utils/dates';
import { getPeriodText } from '../utils';
import { initialFilterTableState } from '../../../../utils/__mocks__/table-filters';
import { useMembersContext } from '../../../members-context';
import { useLocalStorageTableFilters } from '../../../../utils/table-filters/hooks/use-local-storage-table-filters/use-local-storage-table-filters';
import { useTableFilters } from '../../../../utils/table-filters/hooks/use-table-filters/use-table-filters';
import { VirtualizedList, VIRTUALIZED_LIST_TYPE } from '../../../../ui/virtualized-list/virtualized-list';

import * as styles from '../document-library.scss';
import { usePredictionsInfoContext } from '../../../../forecast/detail/context/predictions-info-context';

export interface NormalizedReportScheduleItem
  extends Omit<DispatchedReportItem, 'users' | 'period' | 'next_generate_at'> {
  next_generate_at: Date;
  users: string;
  time: string;
  period: string;
}

const columns = [
  {
    display_name: <FormattedMessage id="reporting.pdf_name" defaultMessage="PDF name" />,
    name: TABLE_COLUMN.REPORT_NAME,
    classes: clsx(styles.flex2)
  },
  {
    display_name: <FormattedMessage id="reporting.created_on" defaultMessage="Created on" />,
    name: TABLE_COLUMN.NEXT_GENERATE_AT,
    classes: clsx(styles.flex1)
  },
  {
    display_name: <FormattedMessage id="reporting.at_time" defaultMessage="At time" />,
    name: TABLE_COLUMN.TIME,
    classes: clsx(styles.flex1)
  },
  {
    display_name: <FormattedMessage id="common.repeating" defaultMessage="Repeating" />,
    name: TABLE_COLUMN.PERIOD,
    classes: clsx(styles.flex2)
  },
  {
    display_name: <FormattedMessage id="reporting.held_for_review" defaultMessage="Held for review" />,
    name: TABLE_COLUMN.HELD_FOR_REVIEW,
    classes: clsx(styles.flex2),
    hideFilters: true
  },
  {
    display_name: <FormattedMessage id="reporting.sending_to" defaultMessage="Sending to" />,
    name: TABLE_COLUMN.SENDING_TO,
    classes: clsx(styles.flex4)
  }
];

const filterColumns = [TABLE_COLUMN.NEXT_GENERATE_AT];

const MIN_QUERY_LENGTH = 3;

const LOCAL_STORAGE_KEY = 'awaiting-review-filters';

export const AwaitingReview = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<NormalizedReportScheduleItem[]>();
  const { isConsolidation } = usePredictionsInfoContext();
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const { isLoading: isMembersLoading } = useMembersContext();

  const {
    state: filterTableState,
    setInitialData,
    setInitialFiltersAndSort,
    handleCloseFilter,
    handleOpenFilter,
    handleClearColumnFilter,
    handleSortClick,
    handleSubmit
  } = useTableFilters(initialFilterTableState);

  useLocalStorageTableFilters(
    LOCAL_STORAGE_KEY,
    organisationId,
    filterTableState,
    filterColumns,
    setInitialFiltersAndSort
  );

  const { filterAnchorElement, activeColumn, sortByColumn, filters, sort, displayedData } = filterTableState;

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const fetchedData = await getDispatchedReports(
        workspaceId,
        organisationId,
        { status: 'awaiting-permission' },
        isConsolidation
      );
      return fetchedData.map(d => ({
        ...d,
        next_generate_at: new Date(d.created),
        users: d.users
          .map(u => u.email_address)
          .join(', ')
          .trim(),
        time: getHoursMinutes(d.created),
        period: getPeriodText(d.period, d.repeating_frequency)
      }));
    } catch (e) {
      Logger.error(e as string);
      toastError();
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, organisationId, isConsolidation]);

  const fetchInitialData = useCallback(async () => {
    const initialData = await fetchData();
    setData(initialData);
    setInitialData((initialData as unknown) as Data[]);
  }, [fetchData, setInitialData]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const onClearColumnFilter = useCallback(() => handleClearColumnFilter(initialFilterTableState.filters), [
    handleClearColumnFilter
  ]);

  if (isLoading || isMembersLoading) {
    return (
      <div>
        <UILoader isFull />
      </div>
    );
  }

  return (
    <div data-testid="awaitingReviewContent">
      <h1 className={styles.title}>
        <FormattedMessage id="reporting.awaiting_review" defaultMessage="Awaiting review" />
      </h1>
      <VirtualizedList
        data={displayedData}
        handleTableFilterClick={handleOpenFilter}
        filters={filters}
        sortByColumn={sortByColumn}
        type={VIRTUALIZED_LIST_TYPE.AWAITING_REVIEW}
        columns={columns}
        revalidateData={fetchInitialData}
      />
      <Filter
        data={(data as unknown) as Data[]}
        anchorElement={filterAnchorElement}
        handleSubmit={handleSubmit}
        handleClose={handleCloseFilter}
        handleClearColumnFilter={onClearColumnFilter}
        activeColumn={activeColumn}
        sortByColumn={sortByColumn}
        filters={filters}
        handleSortClick={handleSortClick}
        sort={sort}
        minQueryLength={MIN_QUERY_LENGTH}
      />
    </div>
  );
};
